/*
 * cd-header-nav — flyout + mobile drawer behavior for cd-header.
 *
 * Desktop: hover or focus a top-level item with [data-cd-nav-trigger]
 * to open its [data-cd-nav-panel] sibling. Closes on mouseleave of the
 * whole nav-item, ESC, or click outside.
 *
 * Mobile drawer: tap [data-cd-drawer-toggle] to open/close the drawer.
 * Inside the drawer, accordion rows ([data-cd-acc-row]) toggle their
 * [data-cd-acc-panel]. Opening one auto-collapses the others so the
 * drawer never stacks every segment open at once.
 */
(function () {
  /* Per-root init guard (instead of a global window flag) so the theme
     editor's shopify:section:load — which mounts a fresh [data-cd-header]
     in place of the old one — gets rebound. The previous global flag
     early-returned on editor re-renders, leaving flyouts/drawer dead
     until a full page reload. */

  const CLOSE_DELAY = 120; // ms — small grace period when leaving a panel

  function initDesktopFlyouts(root) {
    const items = root.querySelectorAll('[data-cd-nav-item]');
    items.forEach((item) => {
      const trigger = item.querySelector('[data-cd-nav-trigger]');
      const panel = item.querySelector('[data-cd-nav-panel]');
      if (!trigger || !panel) return;

      let closeTimer = null;

      const open = () => {
        clearTimeout(closeTimer);
        // Close any other open panels first so only one is visible
        items.forEach((other) => {
          if (other !== item) other.removeAttribute('data-open');
        });
        item.setAttribute('data-open', '');
        trigger.setAttribute('aria-expanded', 'true');
      };

      const close = () => {
        item.removeAttribute('data-open');
        trigger.setAttribute('aria-expanded', 'false');
      };

      const closeSoon = () => {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(close, CLOSE_DELAY);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', closeSoon);
      trigger.addEventListener('focus', open);
      // Keep open while focus is anywhere inside
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close();
      });
      // Click toggles for touch + keyboard
      trigger.addEventListener('click', (e) => {
        // For anchor triggers, allow navigation if already open (second click)
        if (item.hasAttribute('data-open')) {
          if (trigger.tagName === 'A') return; // let link follow on second click
          close();
        } else {
          e.preventDefault();
          open();
        }
      });
    });

    // Global close on ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        items.forEach((it) => {
          it.removeAttribute('data-open');
          const t = it.querySelector('[data-cd-nav-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
    });

    // Click outside closes everything
    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) {
        items.forEach((it) => it.removeAttribute('data-open'));
      }
    });
  }

  function initDrawer(root) {
    const drawer = root.querySelector('[data-cd-drawer]');
    const toggles = root.querySelectorAll('[data-cd-drawer-toggle]');
    const closeBtns = root.querySelectorAll('[data-cd-drawer-close]');
    if (!drawer || toggles.length === 0) return;

    const setOpen = (open) => {
      drawer.toggleAttribute('data-open', open);
      document.documentElement.toggleAttribute('data-cd-drawer-open', open);
      toggles.forEach((t) => t.setAttribute('aria-expanded', open ? 'true' : 'false'));
    };

    toggles.forEach((t) => {
      t.addEventListener('click', (e) => {
        e.preventDefault();
        setOpen(!drawer.hasAttribute('data-open'));
      });
    });
    closeBtns.forEach((b) => {
      b.addEventListener('click', (e) => {
        e.preventDefault();
        setOpen(false);
      });
    });

    // ESC closes drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.hasAttribute('data-open')) setOpen(false);
    });

    // Backdrop click closes
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) setOpen(false);
    });

    // Accordion rows inside the drawer. Supports nesting: a row can
    // contain other rows inside its panel. Auto-collapse only closes
    // SAME-LEVEL siblings (rows that share the same parent) so opening
    // a nested group doesn't collapse its containing top-level row.
    // When a row opens, its FIRST nested row auto-opens too — that
    // makes the most important group inside a mega ("By Audience" if
    // the merchant ordered it first) visible without a second tap.
    //
    // On init, the FIRST top-level row is opened by default and the
    // recursion cascades into its first nested. So when the user taps
    // the burger and the drawer slides in, Shop Now is already open
    // and By Audience is already expanded — zero taps to see the
    // primary segment collections.
    const rows = drawer.querySelectorAll('[data-cd-acc-row]');
    rows.forEach((row) => {
      // Use direct-child queries so a nested trigger inside this row
      // doesn't get bound twice.
      const trigger = directChild(row, '[data-cd-acc-trigger]');
      const panel = directChild(row, '[data-cd-acc-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const wasOpen = row.hasAttribute('data-open');

        // Close same-level siblings (rows that share the same parent)
        Array.from(row.parentElement.children).forEach((sib) => {
          if (sib !== row && sib.hasAttribute && sib.hasAttribute('data-cd-acc-row')) {
            closeRow(sib);
          }
        });

        if (wasOpen) {
          closeRow(row);
        } else {
          openRow(row);
        }
      });
    });

    // Drawer opens with EVERY top-level row collapsed by default. When
    // the user taps Shop, the openRow recursion (above) auto-opens the
    // "Audience" nested row inside it so they immediately see the
    // audience segments without a second tap.
  }

  function openRow(row) {
    if (!row) return;
    const trigger = directChild(row, '[data-cd-acc-trigger]');
    const panel = directChild(row, '[data-cd-acc-panel]');
    if (!trigger || !panel) return;
    row.setAttribute('data-open', '');
    trigger.setAttribute('aria-expanded', 'true');

    // Find the nested row to auto-open. Prefer one whose trigger text
    // matches "audience" / "shop by" so the customer sees the audience
    // segments first regardless of menu order. Fall back to the first
    // nested row if no name match. Only one nested row opens — the
    // rest stay collapsed so the drawer doesn't show every sub-group
    // expanded at once.
    const nestedRows = [];
    for (const child of panel.children) {
      if (child.hasAttribute && child.hasAttribute('data-cd-acc-row')) {
        nestedRows.push(child);
      }
    }
    if (!nestedRows.length) return;

    let preferred = nestedRows.find((r) => {
      const t = directChild(r, '[data-cd-acc-trigger]');
      const label = (t && t.textContent || '').toLowerCase().trim();
      return label.includes('audience') || label.includes('shop by');
    });
    if (!preferred) preferred = nestedRows[0];

    if (preferred && !preferred.hasAttribute('data-open')) {
      openRow(preferred);
    }
  }

  function closeRow(row) {
    if (!row) return;
    row.removeAttribute('data-open');
    const trigger = directChild(row, '[data-cd-acc-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function directChild(parent, selector) {
    for (const child of parent.children) {
      if (child.matches && child.matches(selector)) return child;
    }
    return null;
  }

  function trackHeaderHeight(root) {
    // The mega panel uses position: fixed and reads --cd-header-h to
    // sit flush under the header band. We track the header's BOTTOM
    // edge (relative to the viewport) — not just its height — so the
    // panel stays glued to the header whether it's in its initial
    // position (with the announcement bar visible above it) or stuck
    // at top: 0 after the announcement scrolls away.
    let pending = false;
    const update = () => {
      pending = false;
      const bottom = Math.round(root.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--cd-header-h', Math.max(0, bottom) + 'px');
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(update);
    };
    update();
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(update).catch(() => {});
    }
    requestAnimationFrame(update);
  }

  function initRoot(root) {
    if (!root || root.dataset.cdHeaderNavInit === '1') return;
    root.dataset.cdHeaderNavInit = '1';
    trackHeaderHeight(root);
    initDesktopFlyouts(root);
    initDrawer(root);
  }

  function init() {
    initRoot(document.querySelector('[data-cd-header]'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target;
    if (!target) return;
    const root = target.matches && target.matches('[data-cd-header]')
      ? target
      : target.querySelector && target.querySelector('[data-cd-header]');
    if (root) initRoot(root);
  });
})();
